// The signing invitation's wording (Documents tab PR 8f).
//
// The email a signer gets ("I've sent you SPDS to sign", the button,
// "It opens in your browser…", "If anything looks wrong, just reply")
// used to be composed in code, in routes/esign.ts. The automation-first
// rule says message wording never lives in code (Grant, 2026-08-11), so
// it lives in a communication_templates row with the slug below, seeded
// with that exact wording, editable in the templates library without a
// deploy. The constants here are the compiled FALLBACK, used only if the
// seeded row is missing. Same shape as the Medicare SOA invitation
// (shared/planning/soa-invitation-email.ts), which keeps its own template.
//
// What a template may say, and what the OS fills in:
//   {{document_name}}          the document, in bold
//   {{deadline_sentence}}      " Please sign by October 19." or nothing
//   {{review_and_sign_button}} the sign button and its plain link
// The greeting ("Hi Robert,"), the sender's note and video, and the
// signature wrap this body and are not part of it: they are per person
// or per send, and the OS always gets them right.
//
// THE BUTTON IS NEVER OPTIONAL. A body that loses the button token (an
// edit in the library, or a sender's edit on one send) still gets the
// button, at the end. An email that asks someone to sign must carry the
// way to sign.
//
// Pure module: no DB, no env. Rendered into HTML on the server and into
// the same blocks for the "Before it goes" preview, so the preview and
// the email cannot disagree.

export const SIGNING_INVITATION_TEMPLATE_SLUG = "esign_signing_invitation";

export const DOCUMENT_NAME_TOKEN = "{{document_name}}";
export const DEADLINE_SENTENCE_TOKEN = "{{deadline_sentence}}";
export const SIGN_BUTTON_TOKEN = "{{review_and_sign_button}}";

/** The longest body a sender may write for one send (the note has its
 *  own 2,000-character limit). */
export const MAX_INVITATION_BODY_CHARS = 4000;

/** Today's wording, word for word, as tokens. */
export const SIGNING_INVITATION_FALLBACK_BODY = `I've sent you {{document_name}} to sign.

{{review_and_sign_button}}

It opens in your browser — there's nothing to download and no account to create. It works on your phone too, and usually takes about a minute.{{deadline_sentence}}

If anything looks wrong, just reply to this email.`;

export const SIGNING_INVITATION_FALLBACK_SUBJECT = "Signature requested: {{document_name}}";

/** One run of text in a line; bold where the OS filled something in. */
export interface InvitationRun {
  text: string;
  bold?: boolean;
}

export type InvitationBlock =
  | { kind: "text"; runs: InvitationRun[] }
  | { kind: "button" };

/** The document's name as the email says it: no extension, no dashes. */
export function invitationDocumentName(filename: string): string {
  return (
    filename
      // A package title is "Contract.pdf + 3 more": the extension sits
      // before the suffix, so strip it there too.
      .replace(/\.[a-z0-9]{2,5}(?=\s\+\s\d+\smore$)/i, "")
      .replace(/\.[a-z0-9]{2,5}$/i, "")
      .replace(/[-_]+/g, " ")
      .trim() || filename
  );
}

/**
 * The body as blocks: paragraphs split on blank lines, tokens filled in,
 * a paragraph left empty by its tokens dropped, and the button always
 * present exactly where the token is, or last.
 */
export function invitationBlocks(
  bodyTemplate: string,
  values: { documentName: string; deadline: string | null },
): InvitationBlock[] {
  const blocks: InvitationBlock[] = [];
  let sawButton = false;
  const paragraphs = bodyTemplate
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== "");
  for (const para of paragraphs) {
    const parts = para.split(SIGN_BUTTON_TOKEN);
    parts.forEach((part, i) => {
      if (i > 0) {
        // Only the first button counts; a second token is dropped rather
        // than putting two buttons in one email.
        if (!sawButton) blocks.push({ kind: "button" });
        sawButton = true;
      }
      const runs = fillRuns(part.trim(), values);
      if (runs.some((r) => r.text.trim() !== "")) blocks.push({ kind: "text", runs });
    });
  }
  if (!sawButton) blocks.push({ kind: "button" });
  return blocks;
}

function fillRuns(text: string, values: { documentName: string; deadline: string | null }): InvitationRun[] {
  const runs: InvitationRun[] = [];
  const pattern = /\{\{(document_name|deadline_sentence)\}\}/g;
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index) });
    if (m[1] === "document_name") {
      runs.push({ text: values.documentName, bold: true });
    } else if (values.deadline) {
      runs.push({ text: " Please sign by " });
      runs.push({ text: values.deadline, bold: true });
      runs.push({ text: "." });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last) });
  return runs;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** The blocks as email HTML. Everything typed is escaped; the button is
 *  the OS's own markup, passed in. */
export function invitationBlocksToHtml(blocks: readonly InvitationBlock[], buttonHtml: string): string {
  return blocks
    .map((b) => {
      if (b.kind === "button") return buttonHtml;
      const inner = b.runs
        .map((r) => {
          const t = escapeHtml(r.text).replace(/\n/g, "<br/>");
          return r.bold ? `<strong>${t}</strong>` : t;
        })
        .join("");
      return `<p>${inner}</p>`;
    })
    .join("");
}

/** The subject from the template's subject line, for a send whose sender
 *  set none. */
export function invitationSubject(subjectTemplate: string, documentName: string): string {
  return subjectTemplate.split(DOCUMENT_NAME_TOKEN).join(documentName).trim() || documentName;
}

/**
 * A body a sender wrote for one send, made safe to keep: text only,
 * capped, and null when it is empty or no different from the standard
 * wording (so "What was sent" only shows a body that really changed).
 */
export function cleanInvitationBody(raw: unknown, standard: string): string | null {
  if (typeof raw !== "string") return null;
  const body = raw.replace(/\r\n?/g, "\n").trim();
  if (body === "" || body.length > MAX_INVITATION_BODY_CHARS) return null;
  if (normalise(body) === normalise(standard)) return null;
  return body;
}

function normalise(s: string): string {
  return s.replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n").trim();
}

// ── The sender's view of the tokens ────────────────────────────────
// In the "Before it goes" editor the tokens read as plain bracketed
// words, so nobody has to type braces. Converted back before sending.

const FRIENDLY: ReadonlyArray<readonly [string, string]> = [
  [DOCUMENT_NAME_TOKEN, "[document name]"],
  [DEADLINE_SENTENCE_TOKEN, "[deadline]"],
  [SIGN_BUTTON_TOKEN, "[Review & sign button]"],
];

export function toFriendlyInvitation(body: string): string {
  return FRIENDLY.reduce((s, [token, word]) => s.split(token).join(word), body);
}

export function fromFriendlyInvitation(body: string): string {
  return FRIENDLY.reduce((s, [token, word]) => s.split(word).join(token), body);
}
